import { useCallback, useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "@/hooks";
import Router, { useRouter } from "next/router";

import {
  isValidEmail,
  isValidMobile,
  matchCountryRegex,
  sentryLogs,
  timezone,
} from "@/utils";

import theme from "@/foundation/Theme";

import * as actions from "@/redux/LandingPageReducer";
import { resetNutritionCallbackValues } from "@/redux/LandingPageReducer";

import logo from "../../../../static/logo-mh-razorpay.png";
import { APP_LOCATION, RAZOR_KEY_ID } from "@/config";
import { DEFAULT_API_ERROR_MESSAGE } from "@/utils/Constants";

const useFormHandler = ({
  methods,
  mapUserData,
  pricing,
  setSuccess,
  setIsLoading,
}) => {
  const dispatch = useAppDispatch();
  const history = useRouter();

  const { promoCodeSuccess: isPromoCodeApplied, promoCodeData: pageData } =
    useAppSelector((state) => state.landingPage);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    formState: { errors },
    reset,
    getValues,
  } = methods;

  const handleBlur = () => {
    const { mobile, mobileCountryCode, email } = getValues();

    const isValidContactDetail =
      (isValidMobile(mobile) && matchCountryRegex(mobileCountryCode, mobile)) ||
      isValidEmail(email);
    if (isValidContactDetail) {
      dispatch(
        actions.saveLead({
          data: {
            is_lead: true,
            ...mapUserData(),
          },
        })
      );
    }
  };

  const onSuccessCallback = ({ razorpay_payment_id, amount }) => {
    const apiData = {
      ...mapUserData(),
      is_payment_completed: true,
      payment_mode: "razorpay",
      transaction_id: razorpay_payment_id,
      purchased_plan_slug: pricing?.slug,
      is_callback_request: false,
      amount_paid: amount,
      is_annual_purchase: true,
      special_pack_purchase: true,
    };
    dispatch(actions.saveLead({ data: apiData }))
      .then((response) => {
        if (response?.payload?.status) {
          Router.push({
            pathname: APP_LOCATION + "payments/success",
            query: {
              package_code: apiData?.package_slug,
              leadid: response?.data?.lead_id,
              amount_paid: apiData?.amount_paid,
            },
          });
        } else {
          history.push({
            pathname: APP_LOCATION + "payments/failure",
          });
          sentryLogs(
            "error",
            `LANDINGPAGE - PAYMENT BACKEND CALL FAILURE - ${apiData?.name}`,
            response.data
          );
        }
      })
      .catch((error) => {
        sentryLogs(
          "error",
          `LANDINGPAGE - PAYMENT BACKEND CALL FAILURE - ${apiData?.name}`,
          error
        );
      });
  };

  const onDismiss = () => {
    setIsLoading(false);
    dispatch(
      actions.saveLead({
        data: {
          is_lead: true,
          ...mapUserData(),
          payment_mode: "razorpay",
          payment_stage: "payment_cancel",
        },
      })
    ).then(() => {
      setIsLoading(false);
    });
  };

  const [razorPayError, setRazorPayError] = useState("");

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const openPaymentModal = (
    totalPrice,
    currency,
    desc,
    callback = () => {}
  ) => {
    const { name, mobileCountryCode, mobile, email } = getValues();

    let razorPayOptions = {
      key: RAZOR_KEY_ID,
      amount: totalPrice * 100, //  100 = 1 INR
      currency: currency,
      name: getValues("name"),
      description: desc,
      image: logo,
      handler: function (response) {
        if (response.razorpay_payment_id) {
          onSuccessCallback({ ...response, amount: totalPrice });
        }
      },
      prefill: {
        contact: "",
        email: "",
      },
      notes: {
        name,
        primary_contact: (mobileCountryCode || "+91") + mobile,
        ...(email && {
          email: email,
        }),
        package_slug: mapUserData()?.package_slug,
        nutrition_goal_id: mapUserData()?.nutrition_goal_id,
        urls_arguments: mapUserData()?.urls_arguments,
        purchased_plan_slug: pricing?.slug,
        is_annual_purchase: true,
        special_pack_purchase: true,
        timezone: timezone,
      },
      theme: {
        color: theme.colorsV2.shyft.purple,
      },
      modal: {
        ondismiss: async function () {
          await onDismiss();
        },
      },
    };

    callback();
    razorPayOptions = {
      ...razorPayOptions,
      amount: totalPrice * 100,
      currency: currency,
      description: desc,
      secondaryBuy: false,
      prefill: {
        name,
        contact: (mobileCountryCode || "+91") + mobile,
        email: email ? email : "",
      },
    };

    if (window.Razorpay) {
      let rzp1 = new window.Razorpay(razorPayOptions);
      rzp1.open();
    } else {
      setRazorPayError(DEFAULT_API_ERROR_MESSAGE);
      sentryLogs(
        "error",
        "Razorpay library not loaded hence Razorpay is undefined."
      );
    }
  };

  const openPaymentPage = (data) => {
    const totalPrice = isPromoCodeApplied ? pageData.new_price : data?.price;
    const currency = data?.currency || data?.currency_code;
    const desc = data?.slug || data?.package_duration;
    const callback = () => {
      // dispatch(resetStatusValues());
    };
    openPaymentModal(totalPrice, currency, desc, callback);
  };

  const onSubmit = (state) => {
    setIsLoading(true);
    const _apiData = mapUserData();
    if (state) {
      let apiData = {
        is_callback_request: true,
        ..._apiData,
      };

      dispatch(actions.saveLead({ data: apiData })).then(() => {
        setSuccess(true);
        setIsLoading(false);
        setShowSuccessModal(true);
      });
    } else {
      if (isPromoCodeApplied && pageData.new_price === 0) {
        let apiData = {
          is_free_trial_requested: true,
          is_annual_purchase: true,
          special_pack_purchase: true,
          transactionId: "",
          payment_mode: "",
          order_id: "",
          payer_id: "",
          ..._apiData,
        };

        dispatch(actions.saveLead({ data: apiData })).then(() => {
          setSuccess(true);
          setIsLoading(false);
          setShowSuccessModal(true);
        });
      } else {
        openPaymentPage(pricing);
      }
    }
  };

  const onSubmitCallback = (state) => {
    if (Object.keys(errors)?.length === 0) {
      onSubmit(state);
    }
  };

  const onHide = useCallback(
    () => dispatch(resetNutritionCallbackValues()),
    [dispatch]
  );

  const onHideCallbackModal = useCallback(() => {
    onHide();
    reset();
    setShowSuccessModal(false);
  }, [onHide, reset]);

  const onHideNutritionCallbackModal = useCallback(() => {
    onHideCallbackModal(onHide);
  }, [onHideCallbackModal, onHide]);

  useEffect(() => {
    if (showSuccessModal === true) {
      setTimeout(onHideNutritionCallbackModal, 6000);
    }
  }, [showSuccessModal, onHideNutritionCallbackModal]);

  return {
    handleBlur,
    onSubmitCallback,
  };
};

export default useFormHandler;
